'use client';

import { useId, useMemo, useState, type ReactNode } from 'react';
import { useGreenhouse } from 'contexts/GreenhouseProvider';
import { filterGreenhouseJobs, getJobTeam } from 'libs/greenhouse/greenhouse-functions';
import { parseJobContent } from 'libs/greenhouse/parseJobContent';
import Details from 'components/shared/BaseAccordion/Details';
import Summary from 'components/shared/BaseAccordion/Summary';
import Link from 'components/shared/Link/Link';
import { LinkTargets } from 'types/constants';
import type { SearchableJobsLabels } from './types';

interface SearchableJobsProps {
  labels: SearchableJobsLabels;
  sectionName?: string;
  headline?: ReactNode;
  copy?: ReactNode;
}

const PAGE_SIZE = 10;

const FIELD_CLASS =
  'h-[39px] w-full rounded-md border border-[#d3d8d5] bg-bg-primary px-[14px] text-xs font-medium text-reserve focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-reserve';

export function SearchableJobs({ labels: fields, sectionName, headline, copy }: SearchableJobsProps) {
  const { jobs, jobLocations, jobTeams } = useGreenhouse();
  const instanceId = useId();
  const headingId = `${instanceId}-heading`;
  const [search, setSearch] = useState('');
  const [location, setLocation] = useState('');
  const [team, setTeam] = useState('');
  const [isRemoteOnly, setIsRemoteOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [expandedJobId, setExpandedJobId] = useState<number | null>(null);

  const filteredJobs = useMemo(
    () =>
      filterGreenhouseJobs(jobs, {
        keyword: search,
        locations: location ? [location] : [],
        teams: team ? [team] : [],
        remoteOnly: isRemoteOnly,
      }),
    [jobs, search, location, team, isRemoteOnly],
  );
  const pageCount = Math.ceil(filteredJobs.length / PAGE_SIZE);
  const visibleJobs = filteredJobs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function goToPage(next: number) {
    setPage(next);
    setExpandedJobId(null);
  }

  function handleFilterChange(update: () => void) {
    update();
    goToPage(1);
  }

  return (
    <section
      id={sectionName}
      aria-labelledby={headingId}
      className="bg-[#f8f6f4] px-6 py-24 text-reserve sm:px-[50px] sm:py-[130px]">
      <div className="mx-auto max-w-[1440px]">
        <div id={headingId}>
          {headline ?? (
            <h2 className="mx-auto max-w-[1178px] font-sans text-[clamp(2.5rem,3.62vw,3.25rem)] font-light leading-[0.95] tracking-[-0.005em] text-kale sm:leading-[0.9]">
              {fields.headline}
            </h2>
          )}
        </div>
        {copy}
        <div className="mx-auto mt-12 max-w-[1026px] sm:mt-20">
          <div>
            <div>
              <div>
                <label htmlFor={`${instanceId}-search`} className="sr-only">
                  {fields.searchLabel}
                </label>
                <input
                  id={`${instanceId}-search`}
                  type="search"
                  value={search}
                  onChange={event => handleFilterChange(() => setSearch(event.target.value))}
                  placeholder={fields.searchPlaceholder}
                  className={`${FIELD_CLASS} placeholder:text-reserve`}
                />
              </div>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-center">
              <div>
                <label htmlFor={`${instanceId}-location`} className="sr-only">
                  {fields.locationLabel}
                </label>
                <select
                  id={`${instanceId}-location`}
                  value={location}
                  onChange={event => handleFilterChange(() => setLocation(event.target.value))}
                  className={FIELD_CLASS}>
                  <option value="">{fields.allLocationsLabel}</option>
                  {jobLocations.map(value => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor={`${instanceId}-team`} className="sr-only">
                  {fields.teamLabel}
                </label>
                <select
                  id={`${instanceId}-team`}
                  value={team}
                  onChange={event => handleFilterChange(() => setTeam(event.target.value))}
                  className={FIELD_CLASS}>
                  <option value="">{fields.allTeamsLabel}</option>
                  {jobTeams.map(value => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
              <label className="inline-flex cursor-pointer items-center gap-3 text-sm font-medium sm:col-span-2 md:col-span-1">
                <input
                  type="checkbox"
                  checked={isRemoteOnly}
                  onChange={event => handleFilterChange(() => setIsRemoteOnly(event.target.checked))}
                  className="h-5 w-5 accent-reserve focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-reserve"
                />
                {fields.remoteLabel}
              </label>
            </div>
            <button
              type="button"
              onClick={() => goToPage(1)}
              className="mt-[14px] h-10 w-40 rounded-[6px] bg-fern text-base font-medium text-kale focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-reserve">
              {fields.submitLabel}
            </button>
            {visibleJobs.length === 0 && (
              <p role="status" className="mt-14">
                No positions match your search.
              </p>
            )}
            {visibleJobs.length > 0 && (
              <div className="mt-14 divide-y divide-[#cacaca] border-y border-[#cacaca]">
                {visibleJobs.map(job => {
                  const isExpanded = expandedJobId === job.id;
                  const jobTeam = getJobTeam(job);
                  return (
                    <Details key={job.id} open={isExpanded} className="py-5">
                      <Summary
                        label={job.title}
                        isActive={isExpanded}
                        onClick={willOpen => setExpandedJobId(willOpen ? job.id : null)}
                        className="cursor-pointer gap-4 text-left font-normal focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-reserve">
                        <h3 className="min-w-0 flex-1">
                          {jobTeam && (
                            <span className="mb-4 block font-display text-xs font-medium uppercase leading-none tracking-[0.03em] text-kale">
                              {jobTeam}
                            </span>
                          )}{' '}
                          <span className="block break-words text-xl font-normal leading-[1.2] tracking-[-0.005em] text-kale sm:text-2xl sm:leading-[1.15]">
                            {job.title}
                          </span>{' '}
                          <span className="mt-4 block text-sm font-normal leading-[1.3] tracking-[0.02em] text-reserve">
                            {job.location.name}
                          </span>
                        </h3>
                      </Summary>
                      {isExpanded && (
                        <div className="mt-6 border-t border-[#d9e1db] pt-6">
                          <div className="font-sans text-sm leading-[1.4] text-reserve [overflow-wrap:anywhere] [&>*+*]:mt-[1em] [&_:is(h2,h3,h4,h5,h6)]:font-sans! [&_:is(h2,h3,h4,h5,h6)]:font-semibold! [&_:is(h2,h3,h4,h5,h6)]:leading-[1.3] [&_h2]:text-[24px] [&_h3]:text-[18px] [&_:is(h2,h3,h4,h5,h6)_*]:[font-weight:inherit]! [&_a]:underline [&_ul]:list-disc [&_ul]:pl-[1.5em] [&_ol]:list-decimal [&_ol]:pl-[1.5em] [&_li+li]:mt-[0.35em] [&_li>:is(ul,ol)]:mt-[0.35em]">
                            {parseJobContent(job.content)}
                          </div>
                          <Link
                            href={job.absolute_url}
                            target={LinkTargets.NewTab}
                            className="mt-6 inline-flex min-h-10 min-w-40 items-center justify-center rounded-[6px] border-[1.5px] border-reserve px-5 py-[9px] text-base font-medium leading-5 text-kale transition-colors hover:bg-fern focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-reserve">
                            {fields.applyLabel}
                          </Link>
                        </div>
                      )}
                    </Details>
                  );
                })}
              </div>
            )}
            {pageCount > 1 && (
              <nav aria-label={fields.headline} className="mt-[59px] flex items-center justify-center gap-3">
                <button
                  type="button"
                  aria-label="Previous page"
                  disabled={page === 1}
                  onClick={() => goToPage(page - 1)}
                  className="px-4 py-2 text-2xl disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-reserve">
                  <span aria-hidden="true">←</span>
                </button>
                <span className="rounded-[6px] bg-bg-primary px-3 py-2 text-sm" aria-live="polite">
                  {page} {fields.ofLabel} {pageCount}
                </span>
                <button
                  type="button"
                  aria-label="Next page"
                  disabled={page === pageCount}
                  onClick={() => goToPage(page + 1)}
                  className="px-4 py-2 text-2xl disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-reserve">
                  <span aria-hidden="true">→</span>
                </button>
              </nav>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
